import React, { useMemo } from "react";
import { CompanyInfo } from "../../models/company";
import { CustomerOrderResponse, PaymentType } from "../../generated/graphql";
import OrderDetails from "../../components/OrderDetail";
import { LocalisedText, useI18n, getCountryName } from "../../i18n";
import OrderStatusWidget from "../../components/OrderStatusWidget";
import OrderDetailTitle from "../../components/OrderDetailTitle";
import { isNonEmptyStr } from "../../utils/string";
import { getPriceFromString, getPriceWithCurrency } from "../../utils/price";
import TopBar from "../../components/TopBar";

import styles from "./style.module.scss";

interface OrderTrackingScreenProps {
  company: CompanyInfo;
  customerOrder: CustomerOrderResponse;
}

const OrderTrackingScreen: React.FC<OrderTrackingScreenProps> = (
  props: OrderTrackingScreenProps
) => {
  const { customerOrder, company } = props;
  const i18n = useI18n();
  const locale = useMemo(() => {
    return i18n.locale.toString();
  }, [i18n]);
  const hasBillingAddress = useMemo(() => {
    return (
      (isNonEmptyStr(customerOrder.billingAddress?.address1) ||
        isNonEmptyStr(customerOrder.billingAddress?.address2)) &&
      isNonEmptyStr(customerOrder.billingAddress?.country)
    );
  }, [customerOrder.billingAddress]);
  const {
    paymentMethod,
    paymentAmount,
  }: { paymentMethod: string; paymentAmount: string } = useMemo(() => {
    if (customerOrder.payments.length === 0) {
      return { paymentMethod: "", paymentAmount: "" };
    }
    const payment = customerOrder.payments[0];
    const paymentMethod =
      payment.method === PaymentType.Stripe
        ? i18n.translate("paymentMethod.stripe")
        : i18n.translate("paymentMethod.bankTransfer");
    const paymentAmount = getPriceWithCurrency(
      payment.currency,
      getPriceFromString(payment.amount)
    );
    return {
      paymentMethod,
      paymentAmount,
    };
  }, [customerOrder.payments, i18n]);

  const billingAddress = useMemo(() => {
    let address = "";
    if (isNonEmptyStr(customerOrder.billingAddress?.address2)) {
      address = `*** ${customerOrder.billingAddress?.address2}`;
    } else if (isNonEmptyStr(customerOrder.billingAddress?.address1)) {
      address = `${customerOrder.billingAddress?.address1}`;
    }

    return `${address} ${getCountryName(
      customerOrder.billingAddress?.country || "",
      locale
    )}`;
  }, [customerOrder.billingAddress, locale]);

  return (
    <div className={styles.wrapper}>
      <div className={styles.orderInfoWrapper}>
        <TopBar companyInfo={company} />
        <div className={styles.columns}>
          <div className={styles.leftColumn}>
            <OrderDetailTitle customerOrder={customerOrder} />
            <div className={styles.orderStatusWidgetMobile}>
              <OrderStatusWidget
                statusHistories={customerOrder.statusHistories}
                currentStatus={customerOrder.order.status}
                shipmentInfo={{
                  deliverBy: customerOrder.order.deliverBy,
                  trackingNumber: customerOrder.order.trackingNumber,
                }}
              />
            </div>
            <OrderDetails customerOrder={customerOrder} />
            <div className={styles.payments}>
              <div className={styles.paidAmountTitle}>
                {customerOrder.payments.length > 0 &&
                customerOrder.payments[0].method === PaymentType.Stripe ? (
                  <LocalisedText id="screen.orderTrackingScreen.paidAmount" />
                ) : (
                  <LocalisedText id="screen.orderTrackingScreen.paymentMethod" />
                )}
              </div>
              <div className={styles.paymentItem}>
                <div className={styles.paymentMethod}>{paymentMethod}</div>
                {customerOrder.payments.length > 0 &&
                  customerOrder.payments[0].method === PaymentType.Stripe && (
                    <div className={styles.paidAmount}>{paymentAmount}</div>
                  )}
              </div>
            </div>
            <div className={styles.contactInfo}>
              <div className={styles.contactInfoItem}>
                <div className={styles.contactInfoItemTitle}>
                  <LocalisedText id="screen.orderTrackingScreen.contactPerson" />
                </div>
                <div className={styles.contactInfoItemValue}>
                  {customerOrder.shippingInfo.name}
                </div>
              </div>
              {customerOrder.order.shipmentRequired && (
                <div className={styles.contactInfoItem}>
                  <div className={styles.contactInfoItemTitle}>
                    <LocalisedText id="screen.orderTrackingScreen.shippingAddress" />
                  </div>

                  <div className={styles.contactInfoItemValue}>
                    {`*** ${customerOrder.shippingInfo.address2 ||
                      ""} ${getCountryName(
                      customerOrder.shippingInfo.country || "",
                      locale
                    )}`}
                  </div>
                </div>
              )}
              {hasBillingAddress && (
                <div className={styles.contactInfoItem}>
                  <div className={styles.contactInfoItemTitle}>
                    <LocalisedText id="screen.orderTrackingScreen.billingAddress" />
                  </div>

                  <div className={styles.contactInfoItemValue}>
                    {billingAddress}
                  </div>
                </div>
              )}
            </div>
          </div>
          <div className={styles.rightColumn}>
            <div className={styles.orderStatusDesktop}>
              <OrderStatusWidget
                statusHistories={customerOrder.statusHistories}
                currentStatus={customerOrder.order.status}
                shipmentInfo={{
                  deliverBy: customerOrder.order.deliverBy,
                  trackingNumber: customerOrder.order.trackingNumber,
                }}
              />
            </div>
            {/* TODO: Restore contact text */}
            {/* <div className={styles.contactText}>
              <div>
                <LocalisedText id="screen.orderTrackingScreen.contactUs" />
              </div>
              <a className={styles.contactLink} href="mailto:info@mpower.cloud">
                <LocalisedText id="screen.orderTrackingScreen.contactCustomerService" />
              </a>
            </div> */}
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrderTrackingScreen;
